import { FC, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AccountRequest } from "../../domain/account.interface";

export const EMPTY_ACCOUNT: AccountRequest = {
	name: "",
	status: "ACTIVE",
	description: "",
	auth0: {
		audience: "",
		clientIdSPA: "",
		connection: "Username-Password-Authentication",
		connectionId: "",
		orgId: "",
		ssoRedirectUrl: "",
	},
	qlik: {
		clientId: "",
		clientSecret: "",
		userId: "",
		username: "",
		applicationId: "",
	},
	tremendous: {
		apiKey: "",
		url: "https://api.tremendous.com",
	},
};

type AccountFormProps = {
	initialValues?: AccountRequest;
	submitting?: boolean;
	submitLabel: string;
	onSubmit: (data: AccountRequest) => void;
	onCancel: () => void;
};

const AccountForm: FC<AccountFormProps> = ({
	initialValues,
	submitting,
	submitLabel,
	onSubmit,
	onCancel,
}) => {
	const [formData, setFormData] = useState<AccountRequest>(
		initialValues ?? EMPTY_ACCOUNT,
	);

	// Re-seed when switching from one account to another in edit mode.
	useEffect(() => {
		if (initialValues) {
			setFormData(initialValues);
		}
	}, [initialValues]);

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleNestedInputChange = (
		section: "auth0" | "qlik" | "tremendous",
		field: string,
		value: string,
	) => {
		setFormData((prev) => ({
			...prev,
			[section]: { ...prev[section], [field]: value },
		}));
	};

	return (
		<Grid container spacing={3}>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="Account Name"
					value={formData.name}
					onChange={(e) => handleInputChange("name", e.target.value)}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<FormControl fullWidth>
					<InputLabel>Status</InputLabel>
					<Select
						value={formData.status}
						label="Status"
						onChange={(e) => handleInputChange("status", e.target.value)}
					>
						<MenuItem value="ACTIVE">Active</MenuItem>
						<MenuItem value="INACTIVE">Inactive</MenuItem>
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={12}>
				<TextField
					fullWidth
					label="Description"
					value={formData.description ?? ""}
					onChange={(e) => handleInputChange("description", e.target.value)}
					multiline
					rows={2}
				/>
			</Grid>

			{/* Auth0 */}
			<Grid item xs={12}>
				<Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
					Auth0 Configuration
				</Typography>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="Audience"
					value={formData.auth0.audience}
					onChange={(e) =>
						handleNestedInputChange("auth0", "audience", e.target.value)
					}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="Client ID SPA"
					value={formData.auth0.clientIdSPA}
					onChange={(e) =>
						handleNestedInputChange("auth0", "clientIdSPA", e.target.value)
					}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="Connection"
					value={formData.auth0.connection}
					onChange={(e) =>
						handleNestedInputChange("auth0", "connection", e.target.value)
					}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="Connection ID"
					value={formData.auth0.connectionId}
					onChange={(e) =>
						handleNestedInputChange("auth0", "connectionId", e.target.value)
					}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="Organization ID"
					value={formData.auth0.orgId}
					onChange={(e) =>
						handleNestedInputChange("auth0", "orgId", e.target.value)
					}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="SSO Redirect URL"
					value={formData.auth0.ssoRedirectUrl ?? ""}
					onChange={(e) =>
						handleNestedInputChange("auth0", "ssoRedirectUrl", e.target.value)
					}
				/>
			</Grid>

			{/* Qlik */}
			<Grid item xs={12}>
				<Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
					Qlik Configuration
				</Typography>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="Client ID"
					value={formData.qlik.clientId}
					onChange={(e) =>
						handleNestedInputChange("qlik", "clientId", e.target.value)
					}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="Client Secret"
					type="password"
					value={formData.qlik.clientSecret}
					onChange={(e) =>
						handleNestedInputChange("qlik", "clientSecret", e.target.value)
					}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="User ID"
					value={formData.qlik.userId}
					onChange={(e) =>
						handleNestedInputChange("qlik", "userId", e.target.value)
					}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="Username"
					value={formData.qlik.username}
					onChange={(e) =>
						handleNestedInputChange("qlik", "username", e.target.value)
					}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="Application ID"
					value={formData.qlik.applicationId ?? ""}
					onChange={(e) =>
						handleNestedInputChange("qlik", "applicationId", e.target.value)
					}
				/>
			</Grid>

			{/* Tremendous */}
			<Grid item xs={12}>
				<Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
					Tremendous Configuration
				</Typography>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="API Key"
					type="password"
					value={formData.tremendous.apiKey}
					onChange={(e) =>
						handleNestedInputChange("tremendous", "apiKey", e.target.value)
					}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					fullWidth
					label="URL"
					value={formData.tremendous.url}
					onChange={(e) =>
						handleNestedInputChange("tremendous", "url", e.target.value)
					}
					required
				/>
			</Grid>

			<Grid item xs={12}>
				<Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
					<Button variant="outlined" onClick={onCancel} disabled={submitting}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={() => onSubmit(formData)}
						disabled={submitting}
					>
						{submitting ? `${submitLabel}…` : submitLabel}
					</Button>
				</Box>
			</Grid>
		</Grid>
	);
};

export default AccountForm;
